import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, type ReactNode } from "react";
import { EmergencyButton } from "./EmergencyButton";
import { LocaleSwitcher } from "./LocaleSwitcher";
import { focusFirstHeading, SKIP_LINK_CLASSES } from "../lib/a11y";

// Mobile-first layout shell: a content area with a fixed bottom tab
// bar that switches between the two consumer surfaces (Triton chat +
// Alexandria storefront). Top edge has a thin status strip with the
// connection indicator passed in by App.

interface LayoutProps {
  connectionPill: ReactNode;
}

export function Layout({ connectionPill }: LayoutProps) {
  const location = useLocation();
  // Focus the new surface's first heading on route change so screen
  // readers + keyboard nav land on the right element (WCAG 2.4.3).
  useEffect(() => {
    focusFirstHeading();
  }, [location.pathname]);

  return (
    <div className="flex h-full flex-col bg-(--color-canvas) text-(--color-ink)">
      <a href="#main-content" className={SKIP_LINK_CLASSES}>
        Skip to main content
      </a>
      <header className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <span aria-hidden="true" className="text-(--color-accent-gold) text-xl">★</span>
          <span className="text-sm font-medium tracking-wide">Lifestyle</span>
        </div>
        <div className="flex items-center gap-2">
          <LocaleSwitcher />
          <EmergencyButton />
          {connectionPill}
        </div>
      </header>

      <main
        id="main-content"
        tabIndex={-1}
        className="flex-1 min-h-0 overflow-y-auto no-scrollbar"
      >
        <Outlet />
      </main>

      <nav
        aria-label="Primary"
        className="flex justify-around border-t border-white/5 bg-(--color-canvas-elev) px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
      >
        <TabLink to="/triton" label="Triton" hint="Chat" icon={IconChat} />
        <TabLink to="/manage" label="Manage" hint="Current + upcoming + past bookings" icon={IconManage} />
        <TabLink to="/alexandria" label="DealBook" hint="Sealed deals + crawler-curated picks" icon={IconStore} />
        <TabLink to="/memories" label="Memories" hint="Photos + video memories from your trips" icon={IconMemories} />
        <TabLink to="/settings" label="Settings" hint="Autonomy + emergency + visual prefs" icon={IconSettings} />
      </nav>
    </div>
  );
}

function TabLink({
  to,
  label,
  hint,
  icon: Icon,
}: {
  to: string;
  label: string;
  hint: string;
  icon: () => ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          "flex flex-1 flex-col items-center gap-1 rounded-(--radius-pill) py-2 text-xs transition",
          isActive
            ? "text-(--color-accent-gold)"
            : "text-(--color-ink-faint) hover:text-(--color-ink)",
        ].join(" ")
      }
    >
      <span aria-hidden="true"><Icon /></span>
      <span className="leading-none">{label}</span>
      <span className="sr-only">{hint}</span>
    </NavLink>
  );
}

function IconChat() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12c0 4.4-4 8-9 8a10 10 0 0 1-3.7-.7L3 21l1.7-4.4A8 8 0 0 1 3 12c0-4.4 4-8 9-8s9 3.6 9 8z" />
    </svg>
  );
}

function IconStore() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7h16l-1 4a3 3 0 0 1-5.7 0A3 3 0 0 1 8 11a3 3 0 0 1-5-3z" />
      <path d="M5 11v9h14v-9" />
      <path d="M10 20v-5h4v5" />
    </svg>
  );
}

function IconManage() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 5h18M3 12h18M3 19h18" />
      <circle cx="6" cy="5" r="1.5" fill="currentColor" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <circle cx="18" cy="19" r="1.5" fill="currentColor" />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v6M12 17v6M4.2 4.2l4.3 4.3M15.5 15.5l4.3 4.3M1 12h6M17 12h6M4.2 19.8l4.3-4.3M15.5 8.5l4.3-4.3" />
    </svg>
  );
}

function IconMemories() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="11" r="2" />
      <path d="M21 17l-5-5-8 8" />
    </svg>
  );
}
